import { useTranslation } from "react-i18next"

import { RankBlazon } from "@/components/rank/rank-blazon"
import { Card, CardContent } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import type {
  RankLadderNode,
  RankProgress,
  RankWithPrivileges,
} from "@/types/rank"

/**
 * OKS-513 — §11 merged XP→rank card (card 2, Figma `389:437`).
 *
 * Folds the previously-separate XP block, rank progress, privileges and
 * next-tier preview into ONE card so **XP appears exactly once** (PRD §5):
 *   - XP hero "{X} XP" + "+{N} this week"; rank crest (`<RankPatch>`) top-right.
 *   - Tier-journey track (Rookie→…→Champion) from the named `rankLadder`,
 *     with the current node lit.
 *   - "{N} XP to {next}" (or "Top tier" at the summit — `xpToNext` null).
 *   - Reward-peek row "{next} unlocks {privilege}".
 *   - "{N} missions done · +{N} this week".
 *   - "{N} of {M} badges · {K} to unlock" + a progress bar.
 *
 * Progress bars animate in with CSS-only transitions (the shadcn
 * `Progress` primitive's own transform transition — no JS animation).
 */
interface MergedXpRankCardProps {
  xpBalance: number
  xpWeeklyDelta: number
  xpToNext: number | null
  currentRank: RankWithPrivileges | null
  nextRank: RankWithPrivileges | null
  progress: RankProgress | null
  topTier: boolean
  rankLadder: RankLadderNode[]
  missionsDone: number
  missionsDoneThisWeek: number
  badgesTotal: number
  badgesEarnableTotal: number
}

export function MergedXpRankCard({
  xpBalance,
  xpWeeklyDelta,
  xpToNext,
  currentRank,
  nextRank,
  progress,
  topTier,
  rankLadder,
  missionsDone,
  missionsDoneThisWeek,
  badgesTotal,
  badgesEarnableTotal,
}: MergedXpRankCardProps) {
  const { t, i18n } = useTranslation()

  const currentTier = currentRank
    ? (rankLadder.find((node) => node.name === currentRank.name)?.tier ?? 1)
    : 0

  const tierPercent =
    progress && progress.range > 0
      ? Math.min(100, Math.max(0, (progress.current / progress.range) * 100))
      : 0

  const badgesToUnlock = Math.max(0, badgesEarnableTotal - badgesTotal)
  const badgesPercent =
    badgesEarnableTotal > 0
      ? Math.min(100, (badgesTotal / badgesEarnableTotal) * 100)
      : 0

  const firstPrivilege = nextRank?.privileges?.[0]?.text ?? null

  return (
    <Card data-testid="merged-xp-rank-card">
      <CardContent className="flex flex-col gap-5 py-5">
        {/* XP hero + rank crest */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col">
            <span
              data-testid="merged-xp-value"
              className="text-3xl font-bold tracking-tight tabular-nums sm:text-4xl"
            >
              {t("profile.xp_rank.xp_value", {
                value: formatXpNumber(xpBalance, i18n.language),
              })}
            </span>
            {xpWeeklyDelta > 0 && (
              <span
                data-testid="merged-xp-weekly-delta"
                className="text-sm font-medium text-emerald-600"
              >
                {t("profile.xp_rank.weekly_delta", {
                  value: formatXpNumber(xpWeeklyDelta, i18n.language),
                })}
              </span>
            )}
          </div>
          {currentRank && (
            // OKS-539 — frame-less blazon crest (uploaded image or
            // position-tinted default) replaces the generated shield.
            // Decorative — the tier-journey track + "to next" line name
            // the rank.
            <RankBlazon
              input={{
                tier: currentTier,
                tierCount: rankLadder.length || 1,
                name: currentRank.name,
                imageUrl: currentRank.image_url,
              }}
              size="crest"
              decorative
            />
          )}
        </div>

        {/* Tier-journey track */}
        {rankLadder.length > 0 && (
          <ol
            data-testid="merged-tier-journey"
            className="flex items-center justify-between gap-1"
          >
            {rankLadder.map((node) => {
              const reached = node.tier <= currentTier
              return (
                <li
                  key={node.tier}
                  data-tier={node.tier}
                  data-current={node.name === currentRank?.name}
                  className="flex min-w-0 flex-1 flex-col items-center gap-1"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "h-2 w-full rounded-full",
                      reached ? "bg-primary" : "bg-muted",
                    )}
                  />
                  <span
                    className={cn(
                      "max-w-full truncate text-[10px]",
                      node.name === currentRank?.name
                        ? "text-foreground font-semibold"
                        : "text-muted-foreground",
                    )}
                  >
                    {node.name}
                  </span>
                </li>
              )
            })}
          </ol>
        )}

        {/* XP-to-next + tier progress bar */}
        <div className="flex flex-col gap-2">
          {topTier || xpToNext === null || nextRank === null ? (
            <p
              data-testid="merged-xp-to-next-top"
              className="text-muted-foreground text-sm"
            >
              {t("profile.xp_rank.top_tier")}
            </p>
          ) : (
            <>
              <p
                data-testid="merged-xp-to-next"
                className="text-sm font-medium"
              >
                {t("profile.xp_rank.to_next", {
                  value: formatXpNumber(xpToNext, i18n.language),
                  next: nextRank.name,
                })}
              </p>
              <Progress
                value={tierPercent}
                aria-valuemin={0}
                aria-valuemax={progress?.range ?? 0}
                aria-valuenow={progress?.current ?? 0}
              />
              {firstPrivilege && (
                <p
                  data-testid="merged-reward-peek"
                  className="text-muted-foreground text-xs"
                >
                  {t("profile.xp_rank.reward_peek", {
                    next: nextRank.name,
                    privilege: firstPrivilege,
                  })}
                </p>
              )}
            </>
          )}
        </div>

        {/* Missions + badges summary */}
        <div className="border-border flex flex-col gap-3 border-t pt-4">
          <div className="flex items-baseline justify-between">
            <span data-testid="merged-missions" className="text-sm font-medium">
              {t("profile.xp_rank.missions", { value: missionsDone })}
            </span>
            {missionsDoneThisWeek > 0 && (
              <span className="text-xs font-medium text-emerald-600">
                {t("profile.xp_rank.missions_week", {
                  value: missionsDoneThisWeek,
                })}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <span data-testid="merged-badges" className="text-sm font-medium">
                {t("profile.xp_rank.badges", {
                  earned: badgesTotal,
                  total: badgesEarnableTotal,
                })}
              </span>
              {badgesToUnlock > 0 && (
                <span className="text-muted-foreground text-xs">
                  {t("profile.xp_rank.badges_to_unlock", {
                    value: badgesToUnlock,
                  })}
                </span>
              )}
            </div>
            <Progress value={badgesPercent} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
